'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { api } from '../configs/api'
import { useAuth } from './AuthContext'
import { FiltersType, FilterType } from './CommonContext'
import moment from 'moment'

type ModeType = 'pipeline' | 'list'

//* Tipagem dos dados de business
export type PipelineType = {
    id: string
    value: string
    label: string
    steps: PipelineStepType[]
}

export type PipelineStepType = {
    id: string
    title: string
    color?: string
    business: PipelineStepBusinessType[]
}

export type PipelineStepBusinessType = {
    id: string
    title: string
    subtitle?: string
    value?: string
    products?: {
        id: string
        name: string
    }[]
    targets?: FilterType[]
    stepID: string
    dateNotify: string | null
    isExpired: boolean
    professionalID: number | null
}

export type BusinessDataType = {
    pipelines: PipelineType[]
}

type BusinessType = {
    loading: boolean
    startLoading: () => void
    stopLoading: () => void
    filteredBusiness: BusinessDataType
    setFilteredBusiness: (value: BusinessDataType) => void
    business: BusinessDataType
    setBusiness: (value: BusinessDataType) => void
    currentTab: string
    setCurrentTab: (value: string) => void
    currentStep: any
    setCurrentStep: (value: any) => void
    mode: ModeType
    setMode: (value: ModeType) => void
    openDetail: {
        open: boolean
        data: any
    }
    setOpenDetail: (value: { open: boolean; data: any }) => void
    openFilter: boolean
    setOpenFilter: (value: boolean) => void
    filters: FiltersType
    setFilters: React.Dispatch<React.SetStateAction<FiltersType>>
    clearFilters: () => void
    totalFilters: number
    moveBusiness: (id: number, tab: string, stepID: number, updatedTasks: Task[]) => void
    loadBusiness: () => void
    filterByTime: (time: 'today' | 'thisWeek' | 'nextWeek' | 'expired' | null) => void
    handleSearch: (value: string) => void
}

export interface Task {
    id: string
    title: string
    subtitle?: string
    subtitle2?: string
    products?: {
        id: string
        name: string
    }[]
    targets?: FilterType[]
    stepID: string
    dateNotify: string | null
    isExpired: boolean
    professionalID: number | null
}

const Business = createContext<BusinessType | undefined>(undefined)

export const BusinessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { unity } = useAuth()
    const [loading, setLoading] = useState(true)
    const [business, setBusiness] = useState<BusinessDataType>({ pipelines: [] })
    const [filteredBusiness, setFilteredBusiness] = useState<BusinessDataType>({ pipelines: [] })
    const [openDetail, setOpenDetail] = useState({ open: false, data: null })
    const [currentTab, setCurrentTab] = useState('tab1')
    const [currentStep, setCurrentStep] = useState(null)
    const [mode, setMode] = useState<ModeType>('pipeline')
    const startLoading = () => setLoading(true)
    const stopLoading = () => setLoading(false)

    //* Filtros (Etiquetas, Criadores, etc.)
    const [openFilter, setOpenFilter] = useState(false)
    const [filters, setFilters] = useState<FiltersType>({
        search: '',
        products: [],
        targets: [],
        creators: [],
        companies: [],
        time: null
    })

    const clearFilters = () => {
        setFilters({
            search: '',
            products: [],
            targets: [],
            creators: [],
            companies: [],
            time: null
        })
        setFilteredBusiness(business)
    }
    // Conta o total de filtros
    let totalFilters = Object.values(filters).reduce((acc, item) => (Array.isArray(item) ? acc + item.length : acc), 0)
    if (filters.search) totalFilters += 1
    if (filters.time) totalFilters += 1

    const loadBusiness = async () => {
        if (!unity) return

        const response = await fetch(`${api}business/getData`, {
            method: 'POST',
            body: JSON.stringify({
                unityID: unity.unityID
            })
        })
        const result = await response.json()
        setBusiness(result)
        setFilteredBusiness(result)
    }

    const handleSearch = (value: string) => {
        setFilters(prev => ({ ...prev, search: value }))

        // Aplica o filtro combinado (texto + tempo) sempre que o usuário digitar
        applyCombinedFilters(value, filters.time)
    }

    const filterByTime = (time: 'today' | 'thisWeek' | 'nextWeek' | 'expired' | null) => {
        const newTime = filters.time === time ? null : time

        setFilters(prev => ({
            ...prev,
            time: newTime
        }))

        applyCombinedFilters(filters.search, newTime)
    }

    // 🧩 Função unificada para aplicar ambos os filtros (texto e tempo)
    const applyCombinedFilters = (searchValue: string, time: 'today' | 'thisWeek' | 'nextWeek' | 'expired' | null) => {
        // Se nenhum filtro estiver ativo, retorna tudo
        if (!searchValue && !time && !filters.creators.length && !filters.products.length && !filters.targets.length) {
            return setFilteredBusiness(business)
        }

        const today = moment().format('YYYY-MM-DD')
        const startOfWeek = moment().startOf('week')
        const startOfNextWeek = moment().startOf('week').add(7, 'days')

        const filteredPipelines = business.pipelines.map(pipeline => ({
            ...pipeline,
            steps: pipeline.steps.map(step => ({
                ...step,
                business: step.business.filter(biz => {
                    // 🔍 Filtro de busca textual
                    const matchesSearch =
                        !searchValue ||
                        biz.id == searchValue ||
                        biz.title?.toLowerCase().includes(searchValue.toLowerCase()) ||
                        biz.subtitle?.toLowerCase().includes(searchValue.toLowerCase())

                    // ⏰ Filtro por tempo
                    let matchesTime = true
                    if (time && biz.dateNotify) {
                        const date = moment(biz.dateNotify, 'YYYY-MM-DD')
                        switch (time) {
                            case 'today':
                                matchesTime = date.isSame(today, 'day')
                                break
                            case 'thisWeek':
                                matchesTime = date.isBetween(startOfWeek, moment().endOf('week'), 'day', '[]')
                                break
                            case 'nextWeek':
                                matchesTime = date.isBetween(
                                    startOfNextWeek,
                                    startOfNextWeek.clone().endOf('week'),
                                    'day',
                                    '[]'
                                )
                                break
                            case 'expired':
                                matchesTime = date.isBefore(moment(), 'day')
                                break
                        }
                    } else if (time && !biz.dateNotify) {
                        matchesTime = false
                    }

                    // 🥸 Filtro por profissional
                    let matchesProfessionals = true
                    if (filters.creators.length > 0) {
                        matchesProfessionals = filters.creators.some(f => Number(f.id) === Number(biz.professionalID))
                    }

                    // 💰 Filtro por produto
                    let matchesProducts = true
                    if (filters.products.length > 0) {
                        matchesProducts = !!biz.products?.some(p =>
                            filters.products.some(f => Number(f.id) === Number(p.id))
                        )
                    }

                    // 🏷️ Filtro por target
                    let matchesTargets = true
                    if (filters.targets.length > 0) {
                        matchesTargets = !!biz.targets?.some(t =>
                            filters.targets.some(f => Number(f.id) === Number(t.id))
                        )
                    }

                    return matchesSearch && matchesTime && matchesProfessionals && matchesProducts && matchesTargets
                })
            }))
        }))

        // Remove pipelines e steps vazios
        const cleaned = filteredPipelines
            .map(p => ({
                ...p,
                steps: p.steps.filter(s => s.business.length > 0)
            }))
            .filter(p => p.steps.length > 0)

        // ✅ Mantém o formato completo original
        setFilteredBusiness({
            ...business,
            pipelines: cleaned
        })
    }

    const moveBusiness = async (id: number, tab: string, stepID: number, updatedTasks: Task[]) => {
        if (!id || !tab || !stepID) {
            toast.error('Erro ao mover o negócio. Parâmetros ausentes.')
            return
        }

        try {
            startLoading()

            //* Atualiza DB
            const response = await fetch(`${api}business/moveBusiness`, {
                method: 'POST',
                body: JSON.stringify({
                    businessID: id,
                    stepID
                })
            })

            const result = await response.json()
            if (!response.ok) {
                toast.error(result.message || 'Erro desconhecido ao mover o negócio!')
                return
            }

            if (!result.success) {
                toast.error(result.message || 'Erro ao mover o negócio!')
                return
            }

            //* Atualiza contexto
            const updatedBusiness = filteredBusiness.pipelines.map(pipeline => {
                if (pipeline.value === tab) {
                    return {
                        ...pipeline,
                        steps: pipeline.steps.map(step => {
                            // Limpa as tasks da step atual
                            const updatedStep = {
                                ...step,
                                business: [] // Limpa todas as tasks dessa step
                            }

                            // Insere novamente as tasks que pertencem a essa step
                            const tasksForStep = updatedTasks.filter(task => task.stepID == step.id)

                            return {
                                ...updatedStep,
                                business: [...updatedStep.business, ...tasksForStep] // Insere as tasks da step
                            }
                        })
                    }
                }
                return pipeline
            })

            setFilteredBusiness({ pipelines: updatedBusiness })
            setBusiness({ pipelines: updatedBusiness })

            toast.success(result.message)
        } catch (error) {
            console.log(error)
        } finally {
            stopLoading()
        }
    }

    useEffect(() => {
        //* Carrega pipelines => fases => negócios
        loadBusiness()
    }, [unity])

    useEffect(() => {
        applyCombinedFilters(filters.search, filters.time)
    }, [filters.creators, filters.products, filters.targets])

    return (
        <Business.Provider
            value={{
                loading,
                startLoading,
                stopLoading,
                business,
                setBusiness,
                filteredBusiness,
                setFilteredBusiness,
                openDetail,
                setOpenDetail,
                mode,
                setMode,
                currentTab,
                setCurrentTab,
                currentStep,
                setCurrentStep,
                openFilter,
                setOpenFilter,
                filters,
                setFilters,
                clearFilters,
                totalFilters,
                moveBusiness,
                loadBusiness,
                filterByTime,
                handleSearch
            }}
        >
            {children}
        </Business.Provider>
    )
}

export function useBusiness() {
    const context = useContext(Business)
    if (context === undefined) {
        throw new Error('useCreate must be used within a BusinessProvider')
    }
    return context
}
